/**
 * Decompose a parsed library payload into INDEPENDENT review items — one per
 * formula group, rule group and capacity rule — each validated in isolation
 * so a single bad entry errors only its own card, never the whole paste.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FlowLayer, RuleNode } from '@trafficpulse/shared';

import type {
  FormulaGroupPayload,
  FormulaWeightPayload,
} from '@/features/flow-optimizer/clipboard/formula-clipboard.types';

import type {
  CapacityRulePayload,
  ParsedLibraryPayload,
} from '../envelope/library-payload.types';
import { flagMergeableRuleGroups, flagRotationMismatch } from './cross/flags';
import { validateCapacityRule } from './validate-capacity';
import { validateFormulaGroup } from './validate-formula-group';
import { collectRuleGroupItems } from './validate-rule-group';

export interface ReviewIssue {
  level: 'error' | 'warning';
  message: string;
}

export type ReviewItemKind = 'formula-group' | 'rule-group' | 'capacity-rule';

export interface ReviewItem {
  id: string;
  kind: ReviewItemKind;
  label: string;
  /** Layer context for rule groups; capacity rules span their own layers. */
  layer?: FlowLayer;
  /** Sanitized payload halves — exactly ONE is set, matching `kind`. */
  formulaGroup?: FormulaGroupPayload;
  weight?: FormulaWeightPayload;
  ruleGroup?: RuleNode;
  capacityRule?: CapacityRulePayload;
  issues: ReviewIssue[];
  /** Errors ⇒ starts rejected (accept disabled); warnings ⇒ accepted. */
  accepted: boolean;
}

/** Everything the semantic validators may check payloads against. A missing
 *  set simply skips that check (metadata may not be loaded yet). */
export interface LibraryValidationContext {
  metricIds?: ReadonlySet<string>;
  dateRangePresets?: ReadonlySet<string>;
  tagNames?: ReadonlySet<string>;
  /** The wizard's client choice — 'in-place' flags rotation verbs. */
  ruleStyle?: 'rotate' | 'in-place';
}

export function decomposeLibraryPayload(
  parsed: ParsedLibraryPayload,
  ctx: LibraryValidationContext,
): ReviewItem[] {
  return flagRotationMismatch(
    flagMergeableRuleGroups(rawItems(parsed, ctx)),
    ctx,
  );
}

function rawItems(
  parsed: ParsedLibraryPayload,
  ctx: LibraryValidationContext,
): ReviewItem[] {
  switch (parsed.type) {
    case 'group':
      return [validateFormulaGroup(parsed.data, undefined, 0, ctx)];
    case 'formula':
      return formulaItems(parsed.data.groups, parsed.data.groupWeights, ctx);
    case 'rule-set':
      return [
        ...collectRuleGroupItems(parsed.data.excludeRules, ctx),
        ...capacityItems(parsed.data.capacityRules ?? []),
      ];
    case 'package':
      return [
        ...formulaItems(
          parsed.data.formula.groups,
          parsed.data.formula.groupWeights,
          ctx,
        ),
        ...collectRuleGroupItems(parsed.data.rules.excludeRules, ctx),
        ...capacityItems(parsed.data.rules.capacityRules ?? []),
      ];
  }
}

function formulaItems(
  groups: FormulaGroupPayload[],
  weights: ReadonlyArray<FormulaWeightPayload>,
  ctx: LibraryValidationContext,
): ReviewItem[] {
  return groups.map((group, index) =>
    validateFormulaGroup(group, weights[index], index, ctx),
  );
}

function capacityItems(rules: CapacityRulePayload[]): ReviewItem[] {
  return rules.map((rule, index) => validateCapacityRule(rule, index));
}

/** Shared by the validators: issues → initial accepted state. */
export function finishItem(
  item: Omit<ReviewItem, 'accepted'>,
): ReviewItem {
  const hasError = item.issues.some((issue) => issue.level === 'error');
  return { ...item, accepted: !hasError };
}
